import React, { useState, useEffect } from 'react';
import SEO from '../../components/SEO';
import Header from '../../components/ui/Header';
import { useLeadForm } from '../../contexts/LeadFormContext';
import ContactHero from './components/ContactHero';
import ConsultationTypes from './components/ConsultationTypes';
import ContactForm from './components/ContactForm';
import ContactInfo from './components/ContactInfo';
import ProcessTimeline from './components/ProcessTimeline';
import FAQ from './components/FAQ';

const ContactConsultation = () => {
  const { openLeadForm } = useLeadForm();
  const [selectedConsultationType, setSelectedConsultationType] = useState('showroom');
  const [showForm, setShowForm] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleWhatsAppClick = () => {
    openLeadForm();
  };

  const handleConsultationClick = () => {
    setShowForm(true);
    setTimeout(() => {
      document.getElementById('consultation-form')?.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  };

  const handleTypeSelect = (type) => {
    setSelectedConsultationType(type);
    setShowForm(true);
    setTimeout(() => {
      document.getElementById('consultation-form')?.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  };

  const handleFormSubmit = (formData) => {
    console.log('Form submitted:', formData);
    setIsSubmitted(true);
    
    // Simulate success and redirect to WhatsApp
    setTimeout(() => {
      const message = encodeURIComponent(
        `Venho do site da Briza Moveis e gostaria de atendimento.`
      );
      window.open(`https://wa.me/5562993263202?text=${message}`, '_blank');
    }, 2000);
  };

  const getConsultationTypeName = (type) => {
    const types = {
      showroom: 'Visita ao Showroom',
      home: 'Consultoria Domiciliar',
      virtual: 'Consultoria Virtual'
    };
    return types?.[type] || 'Consultoria';
  };

  if (isSubmitted) {
    return (
      <>
        <SEO
          title="Solicitação Enviada | PH Móveis Artesanais"
          description="Sua solicitação de consultoria foi enviada com sucesso. Entraremos em contato em breve."
          noindex={true}
        />
        
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="max-w-md mx-auto text-center p-8">
            <div className="w-20 h-20 bg-success rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="font-playfair text-2xl font-bold text-primary mb-4">
              Solicitação Enviada com Sucesso!
            </h1>
            <p className="text-text-secondary mb-6">
              Recebemos sua solicitação de consultoria. Nossa equipe entrará em contato em até 15 minutos.
            </p>
            <p className="text-sm text-text-secondary">
              Redirecionando para o WhatsApp...
            </p>
          </div>
        </div>
      </>
    );
  }

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contato & Consultoria - PH Móveis Artesanais",
    "description": "Agende sua consultoria gratuita para móveis artesanais personalizados.",
    "url": "https://phmoveis.com.br/contact-consultation",
    "mainEntity": {
      "@type": "LocalBusiness",
      "name": "PH Móveis Artesanais",
      "telephone": "+55-62-99326-3202",
      "email": "phmoveisconteporaneos@gmail.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Rua dos Artesãos, 123",
        "addressLocality": "São Paulo",
        "addressRegion": "SP",
        "postalCode": "05435-000",
        "addressCountry": "BR"
      },
      "openingHours": "Mo-Fr 08:00-18:00, Sa 08:00-14:00",
      "serviceArea": {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": -23.5505,
          "longitude": -46.6333
        },
        "geoRadius": "50000"
      }
    }
  };

  return (
    <>
      <SEO
        title="Contato & Consultoria | Agende Sua Consultoria Gratuita"
        description="Agende sua consultoria gratuita para móveis artesanais personalizados. Atendimento via WhatsApp, telefone ou presencial em São Paulo."
        keywords="consultoria móveis, móveis personalizados, artesanais, São Paulo, WhatsApp, showroom, agendamento, consultoria gratuita"
        image="https://phmoveis.com.br/assets/contact-og.jpg"
        url="https://phmoveis.com.br/contact-consultation"
        type="website"
        structuredData={structuredData}
      />

      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16 lg:pt-20">
          <ContactHero 
            onWhatsAppClick={handleWhatsAppClick}
            onConsultationClick={handleConsultationClick}
          />
          
          <ConsultationTypes 
            onTypeSelect={handleTypeSelect}
            selectedType={selectedConsultationType}
          />
          
          {showForm && (
            <div id="consultation-form">
              <ContactForm 
                selectedType={selectedConsultationType}
                onSubmit={handleFormSubmit}
              />
            </div>
          )}
          
          <ContactInfo 
            onWhatsAppClick={handleWhatsAppClick}
          />
          
          <ProcessTimeline />
          
          <FAQ />
        </main>

        {/* Floating WhatsApp Button */}
        <button
          onClick={handleWhatsAppClick}
          className="fixed bottom-6 right-6 w-14 h-14 bg-accent hover:bg-accent/90 text-accent-foreground rounded-full shadow-premium hover:shadow-premium-lg transition-all duration-300 flex items-center justify-center z-40 animate-pulse hover:animate-none"
          aria-label="Conversar no WhatsApp"
        >
          <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
          </svg>
        </button>
      </div>
    </>
  );
};

export default ContactConsultation;